import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Plus, Copy, Trash2, Eye, EyeOff, KeyRound, Code2, RefreshCw, FileSpreadsheet, Star, ExternalLink, Check, Link2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND}/api/admin/shared-catalogs`;
const RETRIEVE_URL = `${BACKEND}/api/products/retrieve`;
const CSV_URL = `${BACKEND}/api/products/csv`;
const CHANGES_URL = `${BACKEND}/api/products/changes`;
const DOCS_URL = `${window.location.origin}/docs/api`;

const shareLink = (t) => `${window.location.origin}/catalog/${t}`;
const mask = (t) => `cat ${t.slice(4, 8)}...${t.slice(-4)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleString('es-ES') : null);
const copyText = (t, msg = 'Copiado') => {
  navigator.clipboard.writeText(t).then(() => toast.success(msg)).catch(() => toast.error('No se pudo copiar'));
};

const CreateKeyCard = ({ onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const create = async () => {
    setSaving(true);
    try {
      const r = await axios.post(ADMIN_API, { name, description });
      setName(''); setDescription('');
      onCreated(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando la API Key');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Plus className="h-4 w-4" /> Crear Nueva API Key</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div><Label>Nombre *</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Cliente ABC" data-testid="api-key-name-input" /></div>
          <div><Label>Descripción (opcional)</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ej: Acceso solo lectura para integración" data-testid="api-key-description-input" /></div>
        </div>
        <Button onClick={create} disabled={!name.trim() || saving} className="bg-rose-600 hover:bg-rose-700" data-testid="api-key-create-btn">
          <KeyRound className="h-4 w-4 mr-2" /> {saving ? 'Creando...' : 'Crear API Key'}
        </Button>
      </CardContent>
    </Card>
  );
};

const CopyField = ({ value, primary, testid }) => (
  <div className="flex items-center gap-2">
    <code className="flex-1 bg-white/70 border border-emerald-100 rounded-md px-3 py-2.5 font-mono text-sm break-all select-all" data-testid={`${testid}-value`}>{value}</code>
    <Button size="icon" onClick={() => copyText(value)} variant={primary ? 'default' : 'ghost'} className={primary ? 'bg-red-600 hover:bg-red-700 shrink-0' : 'shrink-0'} data-testid={`${testid}-copy`}>
      <Copy className="h-4 w-4" />
    </Button>
  </div>
);

const CreatedKeyBox = ({ k, onClose }) => (
  <div className="border border-emerald-200 bg-emerald-50/70 rounded-xl p-6 space-y-4" data-testid="api-key-created-box">
    <div>
      <div className="flex items-center gap-2 font-semibold text-emerald-700"><Check className="h-5 w-5" /> ¡API Key creada!</div>
      <p className="text-sm text-slate-600 mt-1">Comparte este enlace con tu cliente. Podrá ver y descargar el catálogo.</p>
    </div>
    <div className="space-y-1.5">
      <Label>Enlace para compartir:</Label>
      <CopyField value={shareLink(k.token)} primary testid="api-key-created-link" />
    </div>
    <div className="space-y-1.5">
      <Label className="text-slate-500 font-normal">API Key (para uso técnico):</Label>
      <CopyField value={k.token} testid="api-key-created-token" />
    </div>
    <Button variant="ghost" size="sm" onClick={onClose} data-testid="api-key-created-close">Cerrar</Button>
  </div>
);

const KeyRow = ({ k, onChange }) => {
  const toggle = async () => {
    await axios.put(`${ADMIN_API}/${k.id}`, { name: k.name, description: k.description || '', is_active: !k.is_active });
    toast.success(k.is_active ? 'API Key desactivada' : 'API Key activada');
    onChange();
  };
  const remove = async () => {
    if (!window.confirm(`¿Eliminar la API Key "${k.name}"? El cliente perderá el acceso.`)) return;
    await axios.delete(`${ADMIN_API}/${k.id}`);
    toast.success('API Key eliminada');
    onChange();
  };
  const [downloading, setDownloading] = useState(false);
  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${ADMIN_API}/${k.id}/csv`, { responseType: 'blob', timeout: 300000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `catalogo_keypubli_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV descargado');
    } catch (e) {
      let detail = e.message;
      try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* not JSON */ }
      toast.error(`Error descargando el CSV: ${detail}`);
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="flex items-center justify-between gap-4 border rounded-lg px-4 py-3 bg-slate-50/60 hover:bg-slate-50 transition-colors" data-testid={`api-key-row-${k.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900">{k.name}</span>
          <Badge className={k.is_active ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-200 text-slate-600 hover:bg-slate-200'} data-testid={`api-key-status-${k.id}`}>{k.is_active ? 'Activa' : 'Inactiva'}</Badge>
        </div>
        {k.description && <div className="text-xs text-slate-500">{k.description}</div>}
        <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3">
          <span className="font-mono" data-testid={`api-key-masked-${k.id}`}>{mask(k.token)}</span>
          <span>• {k.request_count || 0} peticiones</span>
          {k.last_used_at && <span>• Último uso: {fmtDate(k.last_used_at)}</span>}
          {k.last_ip && <span>• IP: {k.last_ip}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button size="sm" variant="outline" onClick={downloadCsv} disabled={downloading} data-testid={`api-key-csv-${k.id}`}>
          {downloading ? <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5 mr-1 text-blue-600" />} {downloading ? 'Generando...' : 'CSV'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => copyText(shareLink(k.token), 'Enlace copiado')} data-testid={`api-key-copy-link-${k.id}`}><Link2 className="h-3.5 w-3.5 mr-1" /> Copiar enlace</Button>
        <Button size="sm" variant="outline" onClick={() => copyText(k.token, 'Token copiado')} data-testid={`api-key-copy-${k.id}`}><Copy className="h-3.5 w-3.5 mr-1" /> Copiar token</Button>
        <Button size="icon" variant="ghost" onClick={toggle} title={k.is_active ? 'Desactivar' : 'Activar'} data-testid={`api-key-toggle-${k.id}`}>
          {k.is_active ? <Eye className="h-4 w-4 text-emerald-600" /> : <EyeOff className="h-4 w-4 text-slate-400" />}
        </Button>
        <Button size="icon" variant="ghost" onClick={remove} className="text-red-600" title="Eliminar" data-testid={`api-key-delete-${k.id}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
    </div>
  );
};

const Endpoint = ({ method, url, children, testid }) => (
  <div className="border rounded-lg p-4 bg-white space-y-2">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-xs font-bold text-white px-2 py-0.5 rounded ${method === 'POST' ? 'bg-blue-600' : 'bg-emerald-600'}`}>{method}</span>
        <code className="text-sm font-mono break-all">{url}</code>
      </div>
      <Button size="sm" variant="outline" onClick={() => copyText(url, 'URL copiada')} data-testid={testid}>Copiar URL</Button>
    </div>
    <div className="text-sm text-slate-600 space-y-1">{children}</div>
  </div>
);

const DocsCard = () => (
  <Card>
    <CardHeader>
      <CardTitle className="text-base flex items-center gap-2"><Code2 className="h-4 w-4 text-rose-600" /> Documentación de la API</CardTitle>
      <CardDescription>Endpoints disponibles para acceder al catálogo</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="border border-amber-300 bg-amber-50/60 rounded-lg p-4 space-y-4" data-testid="api-docs-box">
        <div className="font-semibold flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" /> Para compartir con integradores externos (marketplaces, comparadores…)</div>
        <p className="text-sm text-amber-800">Comparte estos endpoints + el token con tu cliente. Solo devuelven medios activos y disponibles, sin datos sensibles.</p>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">Documentación pública (ES/EN + PDF):</span>
          <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" className="text-rose-600 underline inline-flex items-center gap-1" data-testid="api-docs-public-link">{DOCS_URL} <ExternalLink className="h-3 w-3" /></a>
          <Button size="sm" variant="outline" onClick={() => copyText(DOCS_URL, 'Enlace copiado')} data-testid="api-docs-copy-link">Copiar enlace</Button>
        </div>
        <Endpoint method="POST" url={RETRIEVE_URL} testid="api-docs-copy-retrieve">
          <p>Devuelve el catálogo en JSON. 1000 ítems por página; sin <code>page</code> devuelve todo (máx 50 000).</p>
          <p><b>Autenticación (recomendada):</b> cabecera <code>Authorization: Bearer TU_TOKEN</code>. También se acepta <code>_token</code> en el body o en la URL.</p>
          <p><b>Body (JSON o form):</b> <code>page</code> (opcional) — 1, 2, 3… (1000 webs por página)</p>
          <p>Límites: 120 peticiones/minuto por token (HTTP 429 si se supera). 10 tokens inválidos desde una IP la bloquean 15 min.</p>
          <pre className="bg-slate-900 text-emerald-300 text-xs rounded p-3 overflow-x-auto">{`curl -X POST ${RETRIEVE_URL} \\
  -H "Authorization: Bearer TU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"page":1}'`}</pre>
        </Endpoint>
        <Endpoint method="POST" url={CHANGES_URL} testid="api-docs-copy-changes">
          <p>Solo los cambios desde una fecha: <code>added</code> (nuevos), <code>updated</code> (modificados) y <code>removed</code> (ids retirados). Ideal para sincronizar cada hora.</p>
          <p><b>Body:</b> <code>since</code> (obligatorio, ISO 8601). Cada producto incluye <code>status</code> (new / updated / unchanged), <code>created_at</code> y <code>updated_at</code>.</p>
          <pre className="bg-slate-900 text-emerald-300 text-xs rounded p-3 overflow-x-auto">{`curl -X POST ${CHANGES_URL} \\
  -H "Authorization: Bearer TU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"since":"2026-10-01T00:00:00Z"}'`}</pre>
        </Endpoint>
        <Endpoint method="GET" url={CSV_URL} testid="api-docs-copy-csv">
          <p>Descarga directa del catálogo entero en formato CSV (separador <code>;</code>, UTF-8 BOM — Excel lo abre bien).</p>
          <pre className="bg-slate-900 text-emerald-300 text-xs rounded p-3 overflow-x-auto">{`curl -L ${CSV_URL} \\
  -H "Authorization: Bearer TU_TOKEN" \\
  -o catalogo.csv`}</pre>
        </Endpoint>
      </div>
    </CardContent>
  </Card>
);

export const SharedCatalogsTab = () => {
  const [keys, setKeys] = useState([]);
  const [created, setCreated] = useState(null);
  const load = async () => {
    try {
      const r = await axios.get(ADMIN_API);
      setKeys(r.data);
    } catch {
      toast.error('Error cargando API Keys');
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="catalog-api-tab">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">API del Catálogo</h2>
          <p className="text-sm text-slate-500">Crea API keys para compartir tu catálogo con clientes</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="api-keys-refresh-btn"><RefreshCw className="h-4 w-4 mr-1" /> Actualizar</Button>
      </div>
      {created && <CreatedKeyBox k={created} onClose={() => setCreated(null)} />}
      <CreateKeyCard onCreated={(k) => { setCreated(k); load(); }} />
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4 text-rose-600" /> API Keys ({keys.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {keys.map((k) => <KeyRow key={k.id} k={k} onChange={load} />)}
          {keys.length === 0 && <p className="text-sm text-slate-500 text-center py-8" data-testid="api-keys-empty">Aún no hay API Keys</p>}
        </CardContent>
      </Card>
      <DocsCard />
    </div>
  );
};
